import type { IHeaderGroupParams } from 'ag-grid-community';
import { useEffect, useState, type MouseEvent } from 'react';
import { useCallbackRef } from '../../hooks';
import { safeGridApi } from '../BlotterTable/utils';
import { IconButton } from '../Button';
import { HStack } from '../Core';
import { FormControlSizes } from '../Form/types';
import { ICON_SIZES, IconName } from '../Icons';

interface AgGridColumnGroupToggleHeaderProps extends IHeaderGroupParams {
  columnsInGroup: string[];
  dataTestId?: string;
}

export const AgGridColumnGroupToggleHeader = ({
  api,
  displayName,
  columnsInGroup = [],
  dataTestId = 'group-toggle-header-button',
}: AgGridColumnGroupToggleHeaderProps) => {
  const [allColumnsInGroupCollapsed, setAllColumnsInGroupCollapsed] = useState(false);

  const updateColumnCollapsed = useCallbackRef(() => {
    const safeApi = safeGridApi(api);
    const allColumns = safeApi?.getAllGridColumns();
    if (!allColumns) {
      return;
    }

    const isAllCollapsed = columnsInGroup.every(id => {
      const column = allColumns.find(gridColumn => gridColumn.getColId() === id);
      if (column) {
        return !column.isVisible();
      }
      return true;
    });
    setAllColumnsInGroupCollapsed(isAllCollapsed);
  });

  useEffect(() => {
    updateColumnCollapsed();
  }, [updateColumnCollapsed]);

  const onHandleColumnVisibilityChange = useCallbackRef(() => {
    updateColumnCollapsed();
  });

  const handleToggle = useCallbackRef((ev: MouseEvent) => {
    const safeApi = safeGridApi(api);
    if (!safeApi) {
      return;
    }

    ev.preventDefault();
    ev.stopPropagation();
    safeApi.setColumnsVisible(columnsInGroup, allColumnsInGroupCollapsed);
  });

  useEffect(() => {
    safeGridApi(api)?.addEventListener('columnVisible', onHandleColumnVisibilityChange);
    return () => {
      safeGridApi(api)?.removeEventListener('columnVisible', onHandleColumnVisibilityChange);
    };
  }, [api, onHandleColumnVisibilityChange]);

  const toggleLabel = allColumnsInGroupCollapsed ? 'Show all columns' : 'Collapse columns';

  return (
    <HStack h="100%" alignItems="center" w="100%" justifyContent="flex-start" gap="spacing2">
      {displayName}
      <IconButton
        icon={allColumnsInGroupCollapsed ? IconName.Columns : IconName.ExpandLeft}
        iconSize={ICON_SIZES.SMALL}
        ghost
        size={FormControlSizes.Small}
        onClick={handleToggle}
        data-testid={dataTestId}
        title={toggleLabel}
        aria-label={toggleLabel}
      />
    </HStack>
  );
};
